"use client";

import {
  Box,
  Grid,
  HStack,
  Text,
  Button,
  Flex,
  IconButton,
  Drawer,
  Stack,
  Icon,
  Container,
  Image,
  VStack,
  Switch,
} from "@chakra-ui/react";
import { NextLink } from "@/_components/ui/NextLink";
import { useTranslations, useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { getLocaleFromPath } from "@/_utils";
import { RxCross2, RxHamburgerMenu } from "react-icons/rx";
import { IframeDialog } from "@/_components/ui/IframeDialog";
import { useAuthStore } from "@/_stores/useAuthStore";
import { TbLayoutDashboardFilled } from "react-icons/tb";
import { KnootLogo2026 } from "@/_images/svgs/chakra-icons/Logo";
import { useScrollEffect } from "@/app/[locale]/landing/_hooks/useScrollEffect";
import vnFlag from "@/_images/png/vn.png";
import usFlag from "@/_images/png/us.png";

export interface PublicHeaderProps {
  screenName?: string;
  navbar: React.ReactNode;
  mobileNavbar: (onClose: () => void) => React.ReactNode;
  maxW?: string;
  showBookDemo?: boolean;
}

interface MobileHeaderDrawerProps {
  screenName?: string;
  mobileNavbar: (onClose: () => void) => React.ReactNode;
  drawerActions: React.ReactNode;
}

function MobileHeaderDrawer({
  screenName,
  mobileNavbar,
  drawerActions,
}: MobileHeaderDrawerProps) {
  return (
    <Box display={{ base: "block", lg: "none" }}>
      <Drawer.Root
        size={{ base: "full", md: "sm" }}
        id="header-responsive-menu"
      >
        <Drawer.Backdrop />
        <Drawer.Trigger>
          <IconButton
            aria-label="Menu"
            as={RxHamburgerMenu}
            visual="none-style"
            colorPalette="neutral"
            size="sm"
            p={0.5}
            _hover={{ bg: "neutral.100/20" }}
          />
        </Drawer.Trigger>
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Context>
              {(store) => (
                <>
                  <Drawer.CloseTrigger asChild mt={2} mr={2}>
                    <Icon as={RxCross2} boxSize={6} color="neutral.700" />
                  </Drawer.CloseTrigger>
                  <Drawer.Header p={4}>
                    <Flex alignItems="center" gap={2} justifySelf="start">
                      <KnootLogo2026 boxSize="2.4rem" />
                      <Text
                        fontFamily="heading"
                        fontWeight="regular"
                        textStyle="lg"
                        color="primary.600"
                      >
                        Knoot
                      </Text>
                      {screenName && (
                        <>
                          <Box w="1.5px" h="18px" bg="neutral.300" mx={1} />
                          <Text
                            fontSize="15px"
                            fontWeight="500"
                            fontFamily="heading"
                            color="primary.500"
                          >
                            {screenName}
                          </Text>
                        </>
                      )}
                    </Flex>
                  </Drawer.Header>
                  <Drawer.Body textStyle="xl" mt={4}>
                    <Stack gap={4} alignItems="flex-start">
                      {mobileNavbar(() => store.setOpen(false))}
                    </Stack>
                  </Drawer.Body>
                  <Drawer.Footer>{drawerActions}</Drawer.Footer>
                </>
              )}
            </Drawer.Context>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    </Box>
  );
}

export function PublicHeader({
  screenName,
  navbar,
  mobileNavbar,
  maxW = "1400px",
  showBookDemo = true,
}: PublicHeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const localeFromPath = getLocaleFromPath(pathname);
  const localeFromHook = useLocale();
  const locale = localeFromPath || localeFromHook;
  const t = useTranslations("landing.header");
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const scrollAlpha = useScrollEffect();

  const handleLogin = () => {
    router.push("/auth/login");
  };

  const handleLanguageChange = (newLocale: "vi" | "en") => {
    if (newLocale === locale) return;
    const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";
    router.push(`/${newLocale}${pathWithoutLocale}`);
  };

  const renderLanguageSwitch = () => {
    const isEnglish = locale === "en";

    return (
      <Switch.Root
        checked={isEnglish}
        onCheckedChange={(details) =>
          handleLanguageChange(details.checked ? "en" : "vi")
        }
        size="lg"
        colorPalette="neutral"
        css={{
          "--switch-width": "3.4rem",
          "--switch-height": "2rem",
        }}
        aria-label="Language"
      >
        <Switch.HiddenInput />
        <Switch.Control
          bg="neutral.200"
          overflow="hidden"
          _checked={{ bg: "neutral.200" }}
          shadow="shadow-sm"
        >
          <Flex
            position="absolute"
            left="0.26rem"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            zIndex={0}
            filter="grayscale(100%)"
            opacity={isEnglish ? 0.55 : 0}
            transition="opacity 0.2s ease"
          >
            <Image
              src={vnFlag.src}
              alt=""
              boxSize={6}
              objectFit="contain"
              rounded="full"
              aria-hidden
            />
          </Flex>
          <Flex
            position="absolute"
            right="0.26rem"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            zIndex={0}
            filter="grayscale(100%)"
            opacity={isEnglish ? 0 : 0.55}
            transition="opacity 0.2s ease"
          >
            <Image
              src={usFlag.src}
              alt=""
              boxSize={6}
              objectFit="contain"
              rounded="full"
              aria-hidden
            />
          </Flex>
          <Switch.Thumb
            bg="white"
            boxShadow="sm"
            zIndex={1}
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _checked={{ bg: "white" }}
            scale="0.92"
          >
            <Switch.ThumbIndicator
              fallback={
                <Image
                  src={vnFlag.src}
                  alt="Tiếng Việt"
                  boxSize={8}
                  objectFit="contain"
                />
              }
            >
              <Image
                src={usFlag.src}
                alt="English"
                boxSize={8}
                objectFit="contain"
              />
            </Switch.ThumbIndicator>
          </Switch.Thumb>
        </Switch.Control>
      </Switch.Root>
    );
  };

  const renderActionButtons = (variant: "desktop" | "drawer") => {
    const isDrawer = variant === "drawer";

    return (
      <HStack
        hideBelow={isDrawer ? undefined : "lg"}
        pb={0}
        gap={isDrawer ? 2 : undefined}
        justify="flex-end"
      >
        {token ? (
          <HStack gap={6}>
            {!isDrawer && (
              <VStack gap="0" align="flex-start">
                <Text
                  textStyle={{ base: "sm", md: "xs" }}
                  fontWeight="light"
                  color="neutral.700"
                >
                  Signed in as
                </Text>
                <Text
                  textStyle="lg"
                  my="-4px"
                  fontWeight="semibold"
                  color="neutral.700"
                >
                  {user?.userInfo.firstName}
                </Text>
              </VStack>
            )}
            <HStack gap={1}>
              <Button
                size="xs"
                fontWeight="semibold"
                colorPalette="neutral"
                onClick={handleLogin}
              >
                <TbLayoutDashboardFilled />
                My Portal
              </Button>
              {renderLanguageSwitch()}
            </HStack>
          </HStack>
        ) : (
          <>
            <Button
              visual="none-style"
              textStyle="md"
              fontWeight="light"
              fontFamily="heading"
              colorPalette="primary"
              color="primary.600"
              _hover={{ bg: "neutral.100/20" }}
              onClick={handleLogin}
              size="xs"
            >
              {t("login")}
            </Button>

            {showBookDemo && (
              <IframeDialog
                title="Request a Demo"
                trigger={
                  <Button
                    variant="solid"
                    textStyle="sm"
                    size="xs"
                    fontFamily="heading"
                    fontWeight="light"
                  >
                    {t("book_demo")}
                  </Button>
                }
                iframeHeight={{ base: "500px", md: "700px" }}
                variant="demo"
              />
            )}
            {renderLanguageSwitch()}
          </>
        )}
      </HStack>
    );
  };

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      width="100%"
      zIndex={100}
      py={{ base: 3, md: 2 }}
      bg="neutral.50/85"
      color="neutral.800"
      shadow={scrollAlpha >= 1 ? "header" : "none"}
      transition="all 0.3s ease-in-out"
      backdropFilter="blur(10px)"
    >
      <Container maxW={{ base: "full", md: maxW }} px={{ base: 4, md: 6 }}>
        <Grid
          templateColumns={{
            base: "1fr auto",
            lg: "repeat(12, minmax(0, 1fr))",
          }}
          alignItems="center"
          columnGap={{ base: 4, lg: 6 }}
        >
          {/* Logo + screen name — 3/12 on desktop */}
          <Box gridColumn={{ base: "1", lg: "span 3" }} minW={0}>
            {screenName ? (
              <HStack alignItems="center" gap={4}>
                <NextLink
                  href={`/${locale}`}
                  _hover={{ textDecoration: "none" }}
                  flexShrink={0}
                >
                  <KnootLogo2026 boxSize="2.5rem" />
                </NextLink>
                <VStack alignItems="start" gap={0}>
                  <Text
                    fontFamily="heading"
                    fontWeight="regular"
                    textStyle="2xs"
                    color="primary.600"
                  >
                    Knoot
                  </Text>
                  <Text
                    fontWeight="extrabold"
                    textStyle="xl"
                    color="neutral.600"
                  >
                    {screenName}
                  </Text>
                </VStack>
              </HStack>
            ) : (
              <NextLink
                href={`/${locale}`}
                _hover={{ textDecoration: "none" }}
                flexShrink={0}
              >
                <HStack alignItems="center" gap={2}>
                  <KnootLogo2026 boxSize="2.4rem" />
                  <Text
                    fontFamily="heading"
                    fontWeight="regular"
                    textStyle="lg"
                    color="primary.600"
                  >
                    Knoot
                  </Text>
                </HStack>
              </NextLink>
            )}
          </Box>

          {/* Navbar children — 6/12 centered on desktop */}
          <Box
            gridColumn={{ lg: "span 6" }}
            display={{ base: "none", lg: "flex" }}
            justifyContent="center"
            alignItems="center"
            minW={0}
          >
            {navbar}
          </Box>

          {/* Action buttons + mobile menu — 3/12 on desktop */}
          <Box
            gridColumn={{ base: "2", lg: "span 3" }}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
            minW={0}
          >
            <Box display={{ base: "none", lg: "block" }}>
              {renderActionButtons("desktop")}
            </Box>
            <MobileHeaderDrawer
              screenName={screenName}
              mobileNavbar={mobileNavbar}
              drawerActions={renderActionButtons("drawer")}
            />
          </Box>
        </Grid>
      </Container>
    </Box>
  );
}
